/**
 * Date-only helpers. Calendar dates are represented as ISO strings ("YYYY-MM-DD")
 * and all arithmetic is done in UTC so results never depend on the host timezone.
 */
export type ISODate = string;

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

export function isISODate(value: unknown): value is ISODate {
  if (typeof value !== "string") return false;
  const m = ISO_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return mo >= 1 && mo <= 12 && d >= 1 && d <= daysInMonth(y, mo);
}

export function parts(date: ISODate): { y: number; m: number; d: number } {
  const m = ISO_RE.exec(date);
  if (!m) throw new Error(`Invalid ISO date: ${date}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

export function makeDate(y: number, m: number, d: number): ISODate {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function toISODate(date: Date): ISODate {
  return date.toISOString().slice(0, 10);
}

function toUTC(date: ISODate): number {
  const { y, m, d } = parts(date);
  return Date.UTC(y, m - 1, d);
}

function fromUTC(ms: number): ISODate {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Today's date in the given IANA timezone (default Europe/London). */
export function today(timeZone = "Europe/London", now: Date = new Date()): ISODate {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(now);
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function addDays(date: ISODate, days: number): ISODate {
  return fromUTC(toUTC(date) + days * DAY_MS);
}

/**
 * Adds months, clamping to the end of the month. If `anchorDay` is given the
 * result uses that day where possible (so 31 Jan +1 → 28 Feb, +2 → 31 Mar).
 */
export function addMonths(date: ISODate, months: number, anchorDay?: number): ISODate {
  const { y, m, d } = parts(date);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const day = Math.min(anchorDay ?? d, daysInMonth(ny, nm));
  return makeDate(ny, nm, day);
}

export function addYears(date: ISODate, years: number): ISODate {
  return addMonths(date, years * 12);
}

/** Whole days from a to b (b - a). */
export function diffDays(a: ISODate, b: ISODate): number {
  return Math.round((toUTC(b) - toUTC(a)) / DAY_MS);
}

/** Number of days in the inclusive range [from, to]; 0 if to < from. */
export function daysInclusive(from: ISODate, to: ISODate): number {
  return Math.max(0, diffDays(from, to) + 1);
}

export function compareDates(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minDate(a: ISODate, b: ISODate): ISODate {
  return a <= b ? a : b;
}

export function maxDate(a: ISODate, b: ISODate): ISODate {
  return a >= b ? a : b;
}

export function isWithin(date: ISODate, from: ISODate, to: ISODate): boolean {
  return date >= from && date <= to;
}

export function startOfMonth(date: ISODate): ISODate {
  const { y, m } = parts(date);
  return makeDate(y, m, 1);
}

export function endOfMonth(date: ISODate): ISODate {
  const { y, m } = parts(date);
  return makeDate(y, m, daysInMonth(y, m));
}

/** Overlapping inclusive range, or null. */
export function overlap(
  a: { from: ISODate; to: ISODate },
  b: { from: ISODate; to: ISODate },
): { from: ISODate; to: ISODate } | null {
  const from = maxDate(a.from, b.from);
  const to = minDate(a.to, b.to);
  return from <= to ? { from, to } : null;
}

const displayFormats: Record<string, Intl.DateTimeFormatOptions> = {
  "dd/MM/yyyy": { day: "2-digit", month: "2-digit", year: "numeric" },
  "d MMM yyyy": { day: "numeric", month: "short", year: "numeric" },
};

/** Human display of an ISO date, e.g. "06/04/2025" or "6 Apr 2025". */
export function formatDate(date: ISODate | null | undefined, format = "d MMM yyyy"): string {
  if (!date) return "";
  const opts = displayFormats[format] ?? displayFormats["d MMM yyyy"];
  return new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "UTC" }).format(new Date(toUTC(date)));
}
